import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "Components/PageHeader";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography, Segmented } from "antd";
import { FileExcelOutlined, LoadingOutlined } from "@ant-design/icons";
import { SbTable, SbButton, SbModal, SbNotification, SbTag } from "uiCore";
import { getRedeploymentPlanDetails } from "Api/apiService";
import { ClickableTableWrapperStyled } from "../sharedTable.styles";
import { PageTransitionStyled } from "../talentMobility.animations";
import { useRedeploymentCandidates } from "../useTalentMobilityData";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import useAvailabilityStore from "Store/useAvailabilityStore";
import useRedeploymentPlanStore from "Store/useRedeploymentPlanStore";
import useRedeploymentPlanActions from "./useRedeploymentPlanActions";
import PlanSaveButton from "../components/PlanSaveButton";
import { exportRowsAsCsv } from "Utils/exportCsv.utils";
import { TEXT } from "../talentMobility.constants";
import { PersonNextRolesTable } from "./RedeploymentNestedTables";
import { useRedeploymentRoleColumns, useRedeploymentPeopleColumns } from "./useRedeploymentCenterColumns";
import {
  REDEPLOYMENT_VIEW_MODE,
  REDEPLOYMENT_VIEW_OPTIONS,
  REDEPLOYMENT_VIEW_DESCRIPTION,
  REDUCTION_SUMMARY_CSV_FILENAME,
} from "./RedeploymentCenterV2.constants";
import { buildReductionSummaryCsvRows, isPersonRowExpandable } from "./RedeploymentCenterV2.utils";

import useAnalytics from "Hooks/useAnalytics";
import { CROSS_PAGE_TOUR_PHASE, getTourLegSteps } from "../TalentMobilityManagerV2/TalentMobilityManagerV2.constants";
import useTourStore from "Store/useTourStore";
import SbTourLeg from "uiCore/SbTour/SbTourLeg";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Text } = Typography;

const ANALYTICS_FROM = "talent_mobility_redeployment_center";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.REDEPLOYMENT_CENTER_VIEWED;

const RedeploymentCenterV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate = useNavigate();
  // ── Guided tour: this page's leg of the Talent Mobility tour ───────────────
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const headingRowRef = useRef(null);
  const handOffTour = (phase, route) => {
    setCrossPageTourPhase(phase);
    navigate(route);
  };
  const location = useLocation();
  // API records only — no mock PEOPLE/ROLES demo rows on this page.
  const { people, roles: fetchedRoles, isLoading } = useRedeploymentCandidates();
  const thresholds = useMatchThresholds();
  const availableMap = useAvailabilityStore((state) => state.available);

  const [viewMode, setViewMode] = useState(REDEPLOYMENT_VIEW_MODE.ROLE);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [isLoadingPlan, setIsLoadingPlan] = useState(Boolean(location.state?.loadPlanId));

  // Manual placeholder editing for Target Positions — no HRIS import exists
  // yet (see mockData/talentMobility.mock.js comment on
  // currentPositionCount/targetPositionCount), so edits stay session-only.
  // Held in the shared redeployment plan store (keyed by role id) rather
  // than component state so they survive navigating into the reduction
  // workflow and back, and land on the plan's target_position at submit.
  const targetPositionOverrides = useRedeploymentPlanStore((state) => state.targetPositions);
  const setTargetPosition = useRedeploymentPlanStore((state) => state.setTargetPosition);
  const resetRoleDraft = useRedeploymentPlanStore((state) => state.resetRoleDraft);
  const loadPlan = useRedeploymentPlanStore((state) => state.loadPlan);
  const { hasDraft, isSubmitting, planName, planLabel, isUpdate, savePlan } = useRedeploymentPlanActions();

  // Arriving from View Planning's Edit action — it navigates here right away
  // (see ViewPlanningV2's handleEditPlan) carrying which plan to load as
  // router state, rather than making the user wait on that page first. Fetch
  // the plan's full draft here instead and show the table's own loading
  // state until it lands. The ref guards against firing twice (React 18
  // effect double-invoke in dev, or a re-render before navigate's `replace`
  // below clears the state) — loadPlan's reset() would otherwise wipe
  // whatever the user had already started editing.
  const hasLoadedPlanRef = useRef(false);
  useEffect(() => {
    const loadPlanId = location.state?.loadPlanId;
    if (!loadPlanId || hasLoadedPlanRef.current) return;
    hasLoadedPlanRef.current = true;
    const loadPlanName = location.state?.loadPlanName || "";
    (async () => {
      try {
        const response = await getRedeploymentPlanDetails(loadPlanId);
        loadPlan({
          planId: loadPlanId,
          planName: loadPlanName,
          redeploymentPlans: Array.isArray(response?.redeployment_plans) ? response.redeployment_plans : [],
        });
      } catch (error) {
        SbNotification.error(
          "Unable to load redeployment plan",
          error?.response?.data?.message || "Please try again."
        );
      } finally {
        setIsLoadingPlan(false);
        // Drop the router state so a later re-render, or navigating back to
        // this same history entry, doesn't re-run this load.
        navigate("/talent-mobility/redeployment-center", { replace: true });
      }
    })();
  }, [location.state, loadPlan, navigate]);

  const roles = useMemo(
    () =>
      fetchedRoles.map((role) =>
        targetPositionOverrides[role.id] == null
          ? role
          : { ...role, targetPositionCount: targetPositionOverrides[role.id] }
      ),
    [fetchedRoles, targetPositionOverrides]
  );

  // A role is "reducing" once its target is set below its current headcount —
  // the entry point into the reduction workflow (Screen 4a); everything else
  // stays the general browse table it always was.
  const reducingRoles = useMemo(
    () => roles.filter((role) => role.targetPositionCount < role.currentPositionCount),
    [roles]
  );

  // Clicking anywhere in a row except the input itself (which stops its own
  // propagation) fires this same click's change-reject on the input FIRST,
  // then the row's onClick — so an over-limit target and clicking elsewhere
  // in that row rejects-and-warns, then immediately trips the row-click
  // warning too. Both are independently correct, but for one gesture that's
  // two toasts explaining the same thing. This one-shot flag lets the row
  // click recognize "the input change that just fired already covered this"
  // and skip its own warning — consumed (cleared) by the very next row click
  // regardless of outcome, so it can never linger into an unrelated later
  // click.
  const justRejectedTargetRoleIdRef = useRef(null);

  // Commits on every change (typed or stepped), same as Workforce Planning's
  // identical target input — deferring the commit to blur (as this used to)
  // left the ▲▼ stepper buttons looking broken, since clicking a stepper icon
  // never blurs the input, so a stepped value had nowhere to land. This page
  // only plans reductions — a target above current headcount is rejected
  // (never stored) and explained right away, via the same keyed toast so
  // repeated over-limit clicks refresh one popover instead of stacking.
  // Known tradeoff: typing a multi-digit reduction (e.g. "12") commits each
  // in-progress digit as a real target first (the "1" of "12" briefly lands
  // as a genuine reduction, resetting that role's draft) before the full
  // number lands or gets rejected — accepted since it's the same tradeoff
  // Workforce Planning's target input already lives with.
  const handleTargetPositionChange = useCallback(
    (role, nextValue) => {
      // Cleared mid-edit — InputNumber emits null then. Leave the last
      // committed value in place rather than storing nothing.
      if (!Number.isFinite(nextValue)) return;
      // Raising the target back up TO current stays allowed: that's how a
      // planned reduction is undone. Only going above current is rejected.
      if (nextValue > role.currentPositionCount) {
        justRejectedTargetRoleIdRef.current = role.id;
        SbNotification.warning(
          "Target Positions can't be increased",
          `You can only decrease Target Positions — enter a value at or below Current Positions (${role.currentPositionCount}).`,
          { key: "target-position-limit" }
        );
        return;
      }
      justRejectedTargetRoleIdRef.current = null;
      // A committed change to a different target moves this role's at-risk
      // cutoff, so any employees already marked available for it were chosen
      // against a pool that no longer exists — drop only this role's draft
      // (other roles' drafts and the new target itself stay).
      if (nextValue !== role.targetPositionCount) {
        resetRoleDraft(role.id);
      }
      setTargetPosition(role.id, nextValue);
    },
    [setTargetPosition, resetRoleDraft]
  );

  // Employee View plans people-first: each confirmed Mark as Available (in
  // the nested panel, or on a profile entered from it) walks the person's
  // current role's Target Positions down by one — the store's
  // decrementTargetPosition. It starts from the role's live headcount when
  // no target was edited yet, so register the counts once roles load.
  const setRoleHeadcounts = useRedeploymentPlanStore((state) => state.setRoleHeadcounts);
  useEffect(() => {
    if (!fetchedRoles.length) return;
    setRoleHeadcounts(Object.fromEntries(fetchedRoles.map((role) => [role.id, role.currentPositionCount])));
  }, [fetchedRoles, setRoleHeadcounts]);

  const toggleExpandedRow = useCallback((rowKey) => {
    setExpandedRowKeys((previous) =>
      previous.includes(rowKey) ? previous.filter((key) => key !== rowKey) : [...previous, rowKey]
    );
  }, []);

  // Only Employee View expands rows now (Role View clicks navigate or warn) —
  // switching views still collapses everything so stale keys can't reopen
  // rows on return.
  const handleViewModeChange = useCallback((nextViewMode) => {
    setViewMode(nextViewMode);
    setExpandedRowKeys([]);
  }, []);

  // Workforce Planning's growth-required warning, mirrored for reductions —
  // clicking a row that isn't set to reduce explains why nothing opens
  // instead of silently doing nothing. The fixed key keeps repeated clicks
  // refreshing one popover instead of stacking.
  const showReductionRequiredWarning = useCallback((role) => {
    SbNotification.warning(
      "Target must be below Current Positions",
      `Redeployment Center models reductions only — set Target Positions below ${role.currentPositionCount} to plan a reduction for "${role.title}".`,
      { key: "redeployment-reduction-required" }
    );
  }, []);

  // Only reducing rows navigate into the reduction workflow; every other row
  // (this page caps target at current, so that means target == current) gets
  // the reduction-required warning above — same split as Workforce Planning's
  // openDemand.
  const handleRoleRowClick = useCallback(
    (record) => {
      const justRejectedRoleId = justRejectedTargetRoleIdRef.current;
      justRejectedTargetRoleIdRef.current = null;
      if (record.targetPositionCount < record.currentPositionCount) {
        // The title travels along — the reduction screen's occupants endpoint
        // (jd-active-employees) returns no JD metadata to rebuild it from.
        navigate(
          `/talent-mobility/redeployment-center/reduction/${record.id}` +
            `?target=${record.targetPositionCount}&title=${encodeURIComponent(record.title ?? "")}`
        );
      } else if (justRejectedRoleId !== record.id) {
        showReductionRequiredWarning(record);
      }
      // else: the blur that just fired on this same row already explained
      // why nothing happened — no second toast needed.
    },
    [navigate, showReductionRequiredWarning]
  );

  const handleExportSummaryCsv = useCallback(() => {
    exportRowsAsCsv(buildReductionSummaryCsvRows(reducingRoles, availableMap), REDUCTION_SUMMARY_CSV_FILENAME);
  }, [reducingRoles, availableMap]);

  const roleColumns = useRedeploymentRoleColumns({
    roles,
    thresholds,
    onTargetPositionChange: handleTargetPositionChange,
  });
  const peopleColumns = useRedeploymentPeopleColumns({ people });

  return (
    <PageTransitionStyled>
      <PageHeader
        title={TEXT.BTN_REDEPLOYMENT_CENTER}
        headingRef={headingRowRef}
        titleExtra={
          <>
            <SbTag
              color={isLoadingPlan ? "processing" : isUpdate ? "blue" : "gold"}
              icon={isLoadingPlan ? <LoadingOutlined spin /> : undefined}
              style={{ fontSize: 14 }}
            >
              {isLoadingPlan ? "Plan name..." : planLabel}
            </SbTag>
            <Segmented value={viewMode} onChange={handleViewModeChange} options={REDEPLOYMENT_VIEW_OPTIONS} />
          </>
        }
        subtitle={
          <>
            <Text type="secondary">{REDEPLOYMENT_VIEW_DESCRIPTION[viewMode]}</Text>
          </>
        }
        onBack={() => navigate("/talent-mobility")}
        backLabel={TEXT.BACK_TO_TALENT_MOBILITY}
        actions={
          <>
            <SbButton buttonType="secondary" disabled={!hasDraft || isLoadingPlan} onClick={() => setIsSummaryOpen(true)}>
              View Reduction Summary
            </SbButton>
            <PlanSaveButton
              planTypeLabel="Redeployment Plan"
              hasDraft={hasDraft && !isLoadingPlan}
              isUpdate={isUpdate}
              planName={planName}
              isSubmitting={isSubmitting}
              onSave={savePlan}
            />
            <PlanSaveButton
              planTypeLabel="Redeployment Plan"
              buttonType="secondary"
              closeAfter
              hasDraft={hasDraft && !isLoadingPlan}
              isUpdate={isUpdate}
              planName={planName}
              isSubmitting={isSubmitting}
              onSave={savePlan}
            />
          </>
        }
      />

      <ClickableTableWrapperStyled>
        {viewMode === REDEPLOYMENT_VIEW_MODE.ROLE ? (
          <SbTable
            columns={roleColumns}
            dataSource={roles}
            loading={isLoading || isLoadingPlan}
            rowKey="id"
            sticky
            onRow={(record) => ({ onClick: () => handleRoleRowClick(record) })}
          />
        ) : (
          <SbTable
            columns={peopleColumns}
            dataSource={people}
            loading={isLoading || isLoadingPlan}
            rowKey="id"
            sticky
            onRow={(record) => ({ onClick: () => isPersonRowExpandable(record) && toggleExpandedRow(record.id) })}
            expandable={{
              expandedRowKeys,
              showExpandColumn: false,
              rowExpandable: isPersonRowExpandable,
              expandedRowRender: (record) => <PersonNextRolesTable person={record} />,
            }}
          />
        )}
      </ClickableTableWrapperStyled>

      <SbModal
        open={isSummaryOpen}
        title="Redeployment Reduction Summary"
        onCancel={() => setIsSummaryOpen(false)}
        footer={[
          <SbButton key="export" buttonType="secondary" icon={<FileExcelOutlined />} onClick={handleExportSummaryCsv}>
            Export Redeployment Analysis CSV
          </SbButton>,
          <SbButton key="close" buttonType="primary" onClick={() => setIsSummaryOpen(false)}>
            Close
          </SbButton>,
        ]}
      >
        {reducingRoles.length ? (
          reducingRoles.map((role) => {
            const atRiskCount = Math.max(role.currentPositionCount - role.targetPositionCount, 0);
            const availableCount = role.candidates.filter((candidate) => availableMap[candidate.personId]).length;
            return (
              <div key={role.id} style={{ padding: "8px 0", borderBottom: "1px solid #EEF1F5" }}>
                <strong>{role.title}</strong> — {role.currentPositionCount} → {role.targetPositionCount} ({atRiskCount} at risk, {availableCount} marked available)
              </div>
            );
          })
        ) : (
          <Text type="secondary">No roles are currently marked for reduction. Lower a role's Target Positions below its Current Positions to begin.</Text>
        )}
      </SbModal>

      {/* This page's leg of the Talent Mobility tour */}
      <SbTourLeg
        active={crossPageTourPhase === CROSS_PAGE_TOUR_PHASE.REDEPLOYMENT}
        ready={!isLoading && !isLoadingPlan}
        steps={getTourLegSteps(CROSS_PAGE_TOUR_PHASE.REDEPLOYMENT)}
        resolveTarget={() => headingRowRef.current}
        onNext={() => handOffTour(CROSS_PAGE_TOUR_PHASE.WORKFORCE, "/talent-mobility/workforce-planning")}
        onClose={() => handOffTour(CROSS_PAGE_TOUR_PHASE.FINISH, "/talent-mobility")}
      />
    </PageTransitionStyled>
  );
};

export default RedeploymentCenterV2;
